/* eslint-disable @typescript-eslint/no-explicit-any */

import { Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, DOCUMENT } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { Subject, takeUntil } from 'rxjs';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import * as Waves from 'node-waves';

import { NgRxState } from '@xbaht/common/modules/app-ngrx.module';
import { CoreSidebarService } from '@xbaht/ui/core/components/core-sidebars/core-sidebar/services/core-sidebar.service';
import { CoreMenuService } from '@xbaht/ui/core/components/core-menus/core-menu/services/core-menu.service';
import { CoreConfigService, CoreLoadingScreenService, CoreTranslationService } from '@xbaht/ui/core/services';
import { CoreMenu, CoreMenuItem } from '@xbaht/ui/core/types';

// import { Menu } from './views/localizations/menu/menu';

import { locales } from './views/localizations/menu';

import { environment } from 'environments/environment';

@Component({
  selector: 'xbaht-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  public coreConfig: any;
  public menu!: CoreMenu[];
  public defaultLanguage: string = 'en';
  public appLanguage: string = 'en';

  private _unsubscribe: Subject<void>;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private _title: Title,
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    private _ngrxStore: Store<NgRxState>,
    private _coreConfigService: CoreConfigService,
    private _coreSidebarService: CoreSidebarService,
    private _coreLoadingScreenService: CoreLoadingScreenService,
    private _coreMenuService: CoreMenuService,
    private _coreTranslationService: CoreTranslationService,
    private _translateService: TranslateService
  ) {
    // this.menu = Menu;

    // this._coreMenuService.register('main', this.menu);

    // this._coreMenuService.setCurrentMenu('main');

    this._translateService.addLangs(['en']);

    this._translateService.setDefaultLang('en');

    this._coreTranslationService.translate(...locales);

    this._unsubscribe = new Subject<void>();
  }

  ngOnInit(): void {
    Waves.init();

    this._coreConfigService.config.pipe(takeUntil(this._unsubscribe)).subscribe((config: any) => {
      this.coreConfig = config;

      const appLanguage = this.coreConfig.app.appLanguage || 'en';
      this._translateService.use(appLanguage);

      setTimeout(() => {
        this._translateService.setDefaultLang('en');
        this._translateService.setDefaultLang(appLanguage);
      });

      this._elementRef.nativeElement.classList.remove(
        'vertical-layout',
        'vertical-menu-modern',
        'horizontal-layout',
        'horizontal-menu'
      );

      if (this.coreConfig.layout.type === 'vertical') {
        this._elementRef.nativeElement.classList.add('vertical-layout', 'vertical-menu-modern');
      } else if (this.coreConfig.layout.type === 'horizontal') {
        this._elementRef.nativeElement.classList.add('horizontal-layout', 'horizontal-menu');
      }

      this._elementRef.nativeElement.classList.remove(
        'navbar-floating',
        'navbar-static',
        'navbar-sticky',
        'navbar-hidden'
      );

      if (this.coreConfig.layout.navbar.type === 'navbar-static-top') {
        this._elementRef.nativeElement.classList.add('navbar-static');
      } else if (this.coreConfig.layout.navbar.type === 'fixed-top') {
        this._elementRef.nativeElement.classList.add('navbar-sticky');
      } else if (this.coreConfig.layout.navbar.type === 'floating-nav') {
        this._elementRef.nativeElement.classList.add('navbar-floating');
      } else {
        this._elementRef.nativeElement.classList.add('navbar-hidden');
      }

      this._elementRef.nativeElement.classList.remove('footer-fixed', 'footer-static', 'footer-hidden');

      if (this.coreConfig.layout.footer.type === 'footer-sticky') {
        this._elementRef.nativeElement.classList.add('footer-fixed');
      } else if (this.coreConfig.layout.footer.type === 'footer-static') {
        this._elementRef.nativeElement.classList.add('footer-static');
      } else {
        this._elementRef.nativeElement.classList.add('footer-hidden');
      }

      if (
        this.coreConfig.layout.menu.hidden &&
        this.coreConfig.layout.navbar.hidden &&
        this.coreConfig.layout.footer.hidden
      ) {
        this._elementRef.nativeElement.classList.add('blank-page');
        
        this._renderer.setAttribute(
          this._elementRef.nativeElement.getElementsByClassName('app-content')[0],
          'style',
          'transition:none'
        );
      } else {
        this._elementRef.nativeElement.classList.remove('blank-page');
        
        setTimeout(() => {
          this._renderer.setAttribute(
            this._elementRef.nativeElement.getElementsByClassName('app-content')[0],
            'style',
            'transition:300ms ease all'
          );
        }, 0);
        
        if (this.coreConfig.layout.navbar.hidden) {
          this._elementRef.nativeElement.classList.add('navbar-hidden');
        }
        
        if (this.coreConfig.layout.menu.hidden) {
          this._renderer.setAttribute(this._elementRef.nativeElement, 'data-col', '1-column');
        } else {
          this._renderer.removeAttribute(this._elementRef.nativeElement, 'data-col');
        }
        
        if (this.coreConfig.layout.footer.hidden) {
          this._elementRef.nativeElement.classList.add('footer-hidden');
        }
      }

      if (this.coreConfig.layout.skin !== '' && this.coreConfig.layout.skin !== undefined) {
        this.document.body.classList.remove('default-layout', 'bordered-layout', 'dark-layout', 'semi-dark-layout');
        this.document.body.classList.add(this.coreConfig.layout.skin + '-layout');
      }
    });

    if (!environment.production) {
      this._title.setTitle(
        `${this.coreConfig.app.appTitle} (v${environment.version} [${
          environment.mode
        }])`
      );
    } else {
      this._title.setTitle(this.coreConfig.app.appTitle);
    }

    this._ngrxStore.select('Perm').subscribe((item) => {
      this.menu = [
        {
          id: 'dashboard',
          title: 'Dashboard',
          translate: 'MENU.DASHBOARD',
          type: 'item',
          icon: 'home',
          url: '/',
          exactMatch: true
        },
        {
          id: 'settings',
          type: 'section',
          title: 'Settings',
          translate: 'MENU.SETTINGS.SECTION',
          icon: 'package',
          children: []
        }
      ];

      item.permissions.forEach((menu) => {
        const children: CoreMenuItem[] = [];
        menu.permissions.forEach((perm) => {
          children.push({
            id: perm.name.toLowerCase().replace(/ /g, '-'),
            type: 'item',
            title: perm.name,
            translate: `MENU.SETTINGS.${menu.name.toUpperCase().replace(/ /g, '_')}.${perm.name.toUpperCase().replace(/ /g, '_')}`,
            icon: this.mapperIcon(perm.name),
            url: perm.url,
          });
        });

        const ixSec = this.menu?.findIndex(item => item.id === 'settings');
        if (ixSec) {
          this.menu[ixSec].children?.push(
            {
              id: menu.name.toLowerCase().replace(/ /g, '-'),
              type: 'collapsible',
              title: menu.name,
              translate: `MENU.SETTINGS.${menu.name.toUpperCase().replace(/ /g, '_')}.COLLAPSIBLE`,
              classes: 'h5',
              icon: this.mapperGroupIcon(menu.name),
              children: children
            }
          );
        }
      });

      if (this._coreMenuService.getKeys()?.includes('main'))
        this._coreMenuService.unregister('main');

      this._coreMenuService.register('main', this.menu);

      this._coreMenuService.setCurrentMenu('main');
    });
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  public toggleSidebar(key: string | number): void {
    this._coreSidebarService.getSidebarRegistry(key)?.toggleOpen();
  }

  private mapperGroupIcon(value: string): string {
    switch (value.toUpperCase()) {
      case 'CUSTOMER':
        return 'activity';
      case 'COMPLIANCE':
        return 'monitor';
      case 'TRANSACTION':
        return 'database';
      case 'USER MANAGEMENT':
        return 'shield';
      case 'RATE AND FEE':
        return 'layers';
      case 'ALERT AND NOTIFICATION':
        return 'message-square';
      case 'CONFIGURATION':
        return 'settings';
      case 'AUDIT LOG':
        return 'monitor';
      default:
        return 'circle';
    }
  }

  private mapperIcon(value: string): string {
    switch (value.toUpperCase()) {
      case 'CUSTOMER':
        return 'users';
      case 'MONITORING':
        return 'bar-chart-2';
      case 'KYC UPDATE':
        return 'user-check';
      case 'ORDER':
        return 'box';
      case 'USER':
        return 'user';
      case 'ROLE':
        return 'arrow-up-right';
      case 'COUNTRY':
        return 'flag';
      case 'RATE':
        return 'repeat';
      case 'FEE':
        return 'tag';
      case 'PAYOUT PARTNER':
        return 'link-2';
      case 'SYSTEM LIMIT':
        return 'sliders';
      case 'APPLICATION':
        return 'smartphone';
      case 'ACTIVITY LOG':
        return 'alert-circle';
      case 'CAMPAIGN':
        return 'percent';
      case 'NOTIFICATION':
        return 'send';
      default:
        return 'circle';
    }
  }
}